<template>
  <div>
    <div v-if="selectMode">
      <h2>{{ pageTitle }}</h2>
      <p class="action-description">Choose in which session to publish your idea</p>
      <p class="idea">"{{ idea.title }}"</p>
      <div v-if="userLists.length">
        <ul>
          <li
            v-for="(list, index) in userLists"
            v-bind:key="index"
            v-on:click="toggleSelected(list)"
            v-bind:class="{ highlight: selectedListsId.indexOf(list.id) > -1 }"
          >
            {{ list.title }}
          </li>

          <li class="add-new-list">
            <new-input-list :statusDefault="false"></new-input-list>
          </li>
        </ul>
        <primary-button
          class="primary-btn"
          v-bind:act="publishIdeaInTheSelectedLists"
          title="Publish"
        ></primary-button>
      </div>
      <div v-if="userLists.length > -1">
        <ul v-if="userLists.length == 0 && isShowFirstInput">
          <li class="add-new-list">
            <new-input-list :statusDefault="true"></new-input-list>
          </li>
        </ul>
        <empty-state
          v-if="userLists.length == 0 && !isShowFirstInput"
          v-bind:callToAction="callToAction"
          v-bind:description="description"
          v-bind:act="createNewList"
          v-bind:image="emptyStateImage"
          v-bind:primary="true"
        ></empty-state>
      </div>
    </div>
    <div v-if="selectMode == false">
      <h2>Congratulations!</h2>
      <p>You have successfully shared your idea.</p>
      <p>
        We kept a copy of the original idea in "My Ideas"
      </p>
      <p>
        You can find the copies of idea in the sessions:
      </p>
      <div
        class="bold"
        v-for="(list, index) in selectedLists"
        :key="index"
        @click="resetModal()"
      >
        <router-link class="list-link" :to="{ path: '/lists/' + list.id }">
          {{ list.title }}
        </router-link>
      </div>
      <primary-button
        class="primary-btn"
        v-bind:act="resetModal"
        v-bind:title="'Back to my ideas'"
      ></primary-button>
    </div>
  </div>
</template>

<script>
import bus from "../bus";
import api from "../api";
import store from "../store/store";
import getMembersFromList from "../mixins/getMembersFromLists";
import EmptyState from "./EmptyState.vue";
import PrimaryButton from "./shared/PrimaryButton";
import NewInputList from "./lists/NewListInput";

import createIdeasLists from "../gql/ideas_lists/createIdeasLists.gql";
import publishIdea from "../gql/ideas/publishIdea.gql";
import createIdea from "../gql/ideas/createIdea.gql";
import getPublicIdeaByTitle from "../gql/ideas/getPublicIdeaByTitle.gql";

export default {
  data() {
    return {
      selectedListsId: [],
      selectedLists: [],
      id: "",
      isShowFirstInput: false,
      pageTitle: "Publish idea in brainstorming session",
      callToAction: "Create your first session",
      description:
        "It looks a bit empty, as there are no open sessions here. Why not create one?",
      emptyStateImage: "undraw_team_spirit.svg",
      selectMode: true,
      error: null,
    };
  },
  components: {
    EmptyState,
    PrimaryButton,
    NewInputList,
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    userLists: {
      get: function() {
        return this.$store.state.userLists;
      },
      set: function(lists) {
        store.dispatch("fetchUserLists", lists);
      },
    },
  },
  apollo: {
    publicIdeaByTitle: {
      query: getPublicIdeaByTitle,
      variables() {
        return {
          owner_id: this.user.id,
          title: this.idea.title,
        };
      },
      error(error) {
        this.error = JSON.stringify(error.message);
      },
      result() {},
    },
  },
  methods: {
    publishIdeaInTheSelectedLists: async function() {
      if (this.selectedListsId.length == 0 || !this.selectedListsId.length) {
        alert("Select at least one list");
        return;
      }

      let idea_id = null;
      // check if an idea with the same title has been already published
      if (this.publicIdeaByTitle && this.publicIdeaByTitle.length) {
        idea_id = this.publicIdeaByTitle[0].id;
      } else {
        // create an idea clone
        let owner_id = this.user.id;
        let title = this.idea.title;
        let status = "public";

        let res = await this.$apollo.mutate({
          mutation: createIdea,
          variables: {
            owner_id,
            title,
            status,
          },
        });

        idea_id = res.data.insert_ideas_one.id;
      }

      // publish it to the selected lists
      let relationList = [];
      for (let i = 0; i < this.selectedListsId.length; i++) {
        relationList.push({
          list_id: this.selectedListsId[i],
          idea_id: idea_id,
        });
      }

      const objects = relationList;
      this.$apollo
        .mutate({
          mutation: createIdeasLists,
          variables: {
            objects,
          },
        })
        .then(() => {
          const id = this.idea.id;
          this.$apollo
            .mutate({
              mutation: publishIdea,
              variables: {
                id,
              },
            })
            .then(() => {
              api.createEventTracking(
                "idea-published",
                "idea-published",
                this.user.id,
                true
              );

              api.getUserLists(this.user.id);
              bus.$emit("update-ideas");
            });
        })
        .catch(function(error) {
          this.$rollbar.error(error);
          alert("Could not create the brainstorming session. Please try again later.");
        });

      this.selectMode = false;
    },
    getMembersFromLists: function() {
      let members = [];
      let userWaitingToEvaluate = {};

      for (let i = 0; i < this.selectedListsId.length; i++) {
        let list = this.userLists[this.selectedListsId[i]];
        members = [...new Set([...members, ...list.members])];
      }

      userWaitingToEvaluate = this.getMembersFromList(members, this.idea.owner);
      return userWaitingToEvaluate;
    },
    toggleSelected: function(list) {
      let index = this.selectedListsId.indexOf(list.id);
      if (index > -1) {
        this.selectedListsId.splice(index, 1);
        this.selectedLists.splice(index, 1);
      } else {
        this.selectedListsId.push(list.id);
        this.selectedLists.push(list);
      }
    },
    resetModal: function() {
      bus.$emit("close-modal");
    },
    createNewList: function() {
      this.isShowFirstInput = true;
    },
    newListFromModalEvent: function() {
      bus.$on("list-created-from-modal", (newlist) => {
        let lists = JSON.parse(JSON.stringify(this.userLists));
        lists.push(newlist);
        this.userLists = lists;
      });
    },
    modalIsClosedlEvent: function() {
      bus.$on("modal-is-closed", () => {
        this.isShowFirstInput = false;
        this.selectedListsId = [];
        this.selectedLists = [];
      });
    },
  },
  beforeMount: function() {
    this.newListFromModalEvent();
    this.modalIsClosedlEvent();
  },
  props: {
    idea: {
      type: Object,
      required: false,
    },
  },
  mixins: [getMembersFromList],
};
</script>

<style scoped lang="scss">
@use '@/styles/theme.scss';

.publish-modal-content h2 {
  margin-bottom: 0px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.publish-modal-content h2 ~ p {
  margin-top: 5px;
}

.empty-state {
  transform: none;
  position: initial;
  text-align: left;
}

.primary-btn {
  margin-top: 30px;
}

ul {
  padding: 0;
}
ul li {
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
}

ul li:not(:last-child) {
  background: theme.$white-background;
  border: 1px solid theme.$primary-color;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 6px 8px;
  margin-right: 8px;
  margin-bottom: 10px;
  word-break: break-word;
}

p.idea {
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

ul li.highlight {
  background-color: theme.$secondary-color;
}

p.action-description {
  margin-bottom: 0;
}

p.idea {
  margin-bottom: 40px;
}

.add-new-list {
  margin-right: 8px;
  margin-bottom: 10px;
}

.list-link {
  font-size: 14px;
  color: theme.$primary-color;
  font-weight: 600;
}
</style>
